import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronLeft, Plus, MapPin, Trash2, Star } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import {
  addAddress,
  listAddresses,
  removeAddress,
  updateAddress,
  type Address,
} from '@/services/client';

export default function Addresses() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.accessToken);

  const { data: list, isLoading } = useQuery({
    queryKey: ['client/addresses'],
    queryFn: listAddresses,
    enabled: !!token,
  });

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['client/addresses'] });
    qc.invalidateQueries({ queryKey: ['client/me'] });
  };

  const delMut = useMutation({
    mutationFn: (id: string) => removeAddress(id),
    onSuccess: () => { invalidate(); toast.success('Адрес удалён'); },
  });

  const defMut = useMutation({
    mutationFn: (id: string) => updateAddress(id, { isDefault: true }),
    onSuccess: invalidate,
  });

  if (!token) {
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <div className="pt-4 pb-8">
      <div className="px-4 mb-4 flex items-center gap-2">
        <Link to="/profile" className="btn-ghost -ml-2 px-2">
          <ChevronLeft size={20} />
        </Link>
        <h1 className="text-2xl font-semibold flex-1">Мои адреса</h1>
      </div>

      <div className="px-4 space-y-3">
        {isLoading && <div className="card p-6 text-center text-slate-500">Загрузка…</div>}

        {!isLoading && (list?.length ?? 0) === 0 && !showForm && (
          <div className="card p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-2">
              <MapPin size={24} />
            </div>
            <div className="font-medium mb-1">Пока нет адресов</div>
            <div className="text-xs text-slate-500 mb-3">
              Добавьте адрес доставки — он подставится при оформлении заказа.
            </div>
            <button
              className="btn-primary"
              onClick={() => { setEditing(null); setShowForm(true); }}
            >
              <Plus size={16} /> Добавить адрес
            </button>
          </div>
        )}

        {list?.map((a) => (
          <div key={a.id} className="card p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <MapPin size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold truncate">{a.title}</div>
                  {a.isDefault && (
                    <span className="text-[10px] uppercase tracking-wide bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full">
                      По умолчанию
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-600 mt-0.5 break-words">{a.address}</div>
                {(a.entrance || a.floor || a.apartment) && (
                  <div className="text-xs text-slate-500 mt-1">
                    {[a.entrance && `под. ${a.entrance}`, a.floor && `эт. ${a.floor}`, a.apartment && `кв. ${a.apartment}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                )}
                {a.comment && <div className="text-xs text-slate-500 mt-1 italic">{a.comment}</div>}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              {!a.isDefault && (
                <button
                  className="btn-outline flex-1"
                  onClick={() => defMut.mutate(a.id)}
                  disabled={defMut.isPending}
                >
                  <Star size={14} /> По умолчанию
                </button>
              )}
              <button
                className="btn-outline flex-1"
                onClick={() => { setEditing(a); setShowForm(true); }}
              >
                Изменить
              </button>
              <button
                className="btn-outline text-red-600 border-red-200 px-3"
                onClick={() => {
                  if (confirm('Удалить адрес?')) delMut.mutate(a.id);
                }}
                disabled={delMut.isPending}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}

        {showForm && (
          <AddressForm
            initial={editing}
            onClose={() => { setShowForm(false); setEditing(null); }}
            onSaved={() => { invalidate(); setShowForm(false); setEditing(null); }}
          />
        )}

        {!showForm && (list?.length ?? 0) > 0 && (
          <button
            className="btn-outline w-full"
            onClick={() => { setEditing(null); setShowForm(true); }}
          >
            <Plus size={16} /> Добавить адрес
          </button>
        )}
      </div>
    </div>
  );
}

function AddressForm({
  initial,
  onClose,
  onSaved,
}: {
  initial: Address | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [entrance, setEntrance] = useState(initial?.entrance ?? '');
  const [floor, setFloor] = useState(initial?.floor ?? '');
  const [apartment, setApartment] = useState(initial?.apartment ?? '');
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);

  const saveMut = useMutation({
    mutationFn: async () => {
      const body: Partial<Address> = {
        title: title.trim(),
        address: address.trim(),
        entrance: entrance || null,
        floor: floor || null,
        apartment: apartment || null,
        comment: comment || null,
        isDefault,
      };
      if (initial) return updateAddress(initial.id, body);
      return addAddress(body);
    },
    onSuccess: () => {
      toast.success(initial ? 'Адрес обновлён' : 'Адрес добавлен');
      onSaved();
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const canSave = title.trim().length > 0 && address.trim().length > 0;

  return (
    <div className="card p-4 space-y-3">
      <div className="font-semibold">{initial ? 'Изменить адрес' : 'Новый адрес'}</div>

      <div>
        <label className="label text-xs mb-1 block text-slate-500">Название</label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Дом, работа…" />
      </div>

      <div>
        <label className="label text-xs mb-1 block text-slate-500">Адрес</label>
        <textarea
          className="input"
          rows={2}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Улица, дом"
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="label text-xs mb-1 block text-slate-500">Подъезд</label>
          <input className="input" value={entrance ?? ''} onChange={(e) => setEntrance(e.target.value)} />
        </div>
        <div>
          <label className="label text-xs mb-1 block text-slate-500">Этаж</label>
          <input className="input" value={floor ?? ''} onChange={(e) => setFloor(e.target.value)} />
        </div>
        <div>
          <label className="label text-xs mb-1 block text-slate-500">Кв.</label>
          <input className="input" value={apartment ?? ''} onChange={(e) => setApartment(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="label text-xs mb-1 block text-slate-500">Комментарий курьеру</label>
        <textarea
          className="input"
          rows={2}
          value={comment ?? ''}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Код домофона, пожелания…"
        />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        Использовать по умолчанию
      </label>

      <div className="flex gap-2">
        <button className="btn-outline flex-1" onClick={onClose} disabled={saveMut.isPending}>
          Отмена
        </button>
        <button
          className="btn-primary flex-1"
          onClick={() => saveMut.mutate()}
          disabled={!canSave || saveMut.isPending}
        >
          {saveMut.isPending ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
